import api from './api';

const AUTH_STORAGE_KEY = 'user';

// Persist user session data in either localStorage (long-lived) or sessionStorage (current tab)
const storeUserData = (data: any, stayLoggedIn: boolean = true) => {
  const targetStorage = stayLoggedIn ? localStorage : sessionStorage;
  const otherStorage = stayLoggedIn ? sessionStorage : localStorage;

  targetStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
  otherStorage.removeItem(AUTH_STORAGE_KEY);
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Retrieve user data from whichever storage currently holds it, clearing expired tokens
const getStoredUser = () => {
  for (const storage of [sessionStorage, localStorage]) {
    const raw = storage.getItem(AUTH_STORAGE_KEY);
    if (!raw) continue;
    try {
      const data = JSON.parse(raw);
      if (data?.token && isTokenExpired(data.token)) {
        storage.removeItem(AUTH_STORAGE_KEY);
        continue;
      }
      return data;
    } catch {
      storage.removeItem(AUTH_STORAGE_KEY);
    }
  }
  return null;
};

/**
 * Authentication service for handling user authentication
 */
const authService = {
  /**
   * Register a new user
   * @param userData - User registration data
   * @returns Promise with registration response
   */
  register: async (userData: { name: string; email: string; password: string }) => {
    try {
      const response = await api.post('/auth/register', userData);
      if (response.data.token) {
        storeUserData(response.data, true);
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  /**
   * Login a user
   * @param credentials - User login credentials
   * @returns Promise with login response
   */
  login: async (credentials: { email: string; password: string }, stayLoggedIn: boolean = true) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        storeUserData(response.data, stayLoggedIn);
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Logout the current user
   */
  logout: () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  },

  /**
   * Get the current user from localStorage
   * @returns Current user or null if not logged in
   */
  getCurrentUser: () => {
    return getStoredUser();
  },

  /**
   * Get the auth token from localStorage
   * @returns Auth token or null if not logged in
   */
  getAuthToken: () => {
    const user = authService.getCurrentUser();
    return user && user.token ? user.token : null;
  },

  /**
   * Check if user is logged in
   * @returns Boolean indicating if user is logged in
   */
  isLoggedIn: () => {
    return !!authService.getAuthToken();
  },

  /**
   * Check if user is an admin
   * @returns Boolean indicating if user is an admin
   */
  isAdmin: () => {
    const user = authService.getCurrentUser();
    return user && user.user && user.user.role === 'admin';
  }
};

export default authService; 